import { useQuery } from '@tanstack/react-query';
import { Download } from 'lucide-react';
import { useState } from 'react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

import { apiGet, getToken } from '@/api/client';
import { endpoints } from '@/api/endpoints';
import { Button } from '@/components/ui/Button';
import { Card, SectionHeader } from '@/components/ui/Card';
import { Money } from '@/components/ui/Money';
import { PageHeader } from '@/components/ui/PageHeader';
import { StatCard } from '@/components/ui/StatCard';
import { ErrorState, LoadingState } from '@/components/ui/States';

type Period = 'today' | 'this_week' | 'this_month' | 'last_month' | 'this_year';

interface RevenuePoint {
  date: string;
  total: number;
  bills?: number;
  paid?: number;
  due?: number;
}

interface RevenueReport {
  total: number;
  net: number;
  tax: number;
  discount: number;
  bills_count: number;
  daily?: RevenuePoint[];
  top_products?: { name: string; revenue: number; qty: number }[];
  top_customers?: { name: string; total: number }[];
  by_hour?: { hour: number; total: number }[];
  by_weekday?: { weekday: string; total: number }[];
}

const PERIODS: { id: Period; label: string }[] = [
  { id: 'today', label: 'Today' },
  { id: 'this_week', label: 'This week' },
  { id: 'this_month', label: 'This month' },
  { id: 'last_month', label: 'Last month' },
  { id: 'this_year', label: 'This year' },
];

/** Revenue + analytics — pulls from `/reports/revenue` and merges with
 *  `/reports/revenue-analytics` so the page renders even if the
 *  analytics endpoint hasn't been deployed yet. */
export function RevenueReportPage() {
  const [period, setPeriod] = useState<Period>('this_month');

  const summary = useQuery({
    queryKey: ['report', 'revenue', period],
    queryFn: () =>
      apiGet<RevenueReport>(endpoints.reportRevenue, { period }),
  });

  const analytics = useQuery({
    queryKey: ['report', 'revenue-analytics', period],
    queryFn: () =>
      apiGet<Partial<RevenueReport>>(endpoints.reportRevenueAnalytics, { period }),
    // analytics is optional — don't fail the whole page if it's missing
    retry: false,
  });

  if (summary.isLoading) return <LoadingState />;
  if (summary.isError || !summary.data) {
    return (
      <ErrorState
        title="Couldn't load revenue"
        description={(summary.error as Error)?.message}
        onRetry={() => summary.refetch()}
      />
    );
  }

  const r = { ...summary.data, ...(analytics.data ?? {}) };

  return (
    <>
      <PageHeader
        back
        title="Revenue analytics"
        description="Sales totals, peaks, and your top movers."
        actions={<ExportCsv period={period} />}
      />

      {/* Period chips */}
      <div className="flex flex-wrap gap-2 mb-5">
        {PERIODS.map((p) => (
          <button
            key={p.id}
            onClick={() => setPeriod(p.id)}
            className={
              'h-9 px-4 rounded-full text-sm font-medium transition-colors ' +
              (period === p.id
                ? 'bg-brand-600 text-white'
                : 'bg-surface border border-line text-ink-soft hover:text-ink hover:bg-surface-2')
            }
          >
            {p.label}
          </button>
        ))}
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4 mb-6">
        <StatCard label="Total revenue" value={<Money value={r.total ?? 0} />} />
        <StatCard label="Net (after tax)" value={<Money value={r.net ?? 0} />} />
        <StatCard label="Tax collected" value={<Money value={r.tax ?? 0} />} />
        <StatCard label="Bills" value={r.bills_count ?? 0} />
      </div>

      {/* Daily chart */}
      <Card className="mb-4">
        <SectionHeader title="Daily revenue" />
        <DailyChart points={r.daily ?? []} />
      </Card>

      {/* Two-column extras */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-4">
        <Card>
          <SectionHeader title="Peak hours" description="When customers buy most." />
          {(r.by_hour ?? []).length === 0 ? (
            <p className="text-sm text-ink-soft">Not enough data yet.</p>
          ) : (
            <div className="h-56">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={r.by_hour}>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgb(var(--line))" />
                  <XAxis
                    dataKey="hour"
                    stroke="rgb(var(--ink-soft))"
                    fontSize={11}
                    tickFormatter={(h: number) =>
                      h === 0 ? '12am' : h < 12 ? `${h}am` : h === 12 ? '12pm' : `${h - 12}pm`
                    }
                  />
                  <YAxis stroke="rgb(var(--ink-soft))" fontSize={11} width={40} />
                  <Tooltip
                    contentStyle={{
                      background: 'rgb(var(--surface))',
                      border: '1px solid rgb(var(--line))',
                      borderRadius: 8,
                      fontSize: 12,
                    }}
                  />
                  <Bar dataKey="total" fill="rgb(var(--brand-500))" radius={[6, 6, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
        </Card>

        <Card>
          <SectionHeader title="Day of week" description="Which day is your best?" />
          {(r.by_weekday ?? []).length === 0 ? (
            <p className="text-sm text-ink-soft">Not enough data yet.</p>
          ) : (
            <div className="h-56">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={r.by_weekday}>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgb(var(--line))" />
                  <XAxis dataKey="weekday" stroke="rgb(var(--ink-soft))" fontSize={11} />
                  <YAxis stroke="rgb(var(--ink-soft))" fontSize={11} width={40} />
                  <Tooltip
                    contentStyle={{
                      background: 'rgb(var(--surface))',
                      border: '1px solid rgb(var(--line))',
                      borderRadius: 8,
                      fontSize: 12,
                    }}
                  />
                  <Bar dataKey="total" fill="rgb(var(--brand-400))" radius={[6, 6, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
        </Card>
      </div>

      {/* Top lists */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card padded={false}>
          <div className="px-5 py-4 border-b border-line">
            <h2 className="text-base font-semibold">Top products</h2>
          </div>
          {(r.top_products ?? []).length === 0 ? (
            <p className="px-5 py-8 text-sm text-ink-soft text-center">
              Not enough data.
            </p>
          ) : (
            <ul className="divide-y divide-line">
              {(r.top_products ?? []).slice(0, 8).map((p, i) => (
                <li
                  key={i}
                  className="flex items-center justify-between px-5 py-2.5"
                >
                  <span className="text-sm font-medium truncate">{p.name}</span>
                  <span className="text-sm tabular-nums text-ink-soft">
                    {p.qty}× · <Money value={p.revenue} />
                  </span>
                </li>
              ))}
            </ul>
          )}
        </Card>

        <Card padded={false}>
          <div className="px-5 py-4 border-b border-line">
            <h2 className="text-base font-semibold">Top customers</h2>
          </div>
          {(r.top_customers ?? []).length === 0 ? (
            <p className="px-5 py-8 text-sm text-ink-soft text-center">
              Not enough data.
            </p>
          ) : (
            <ul className="divide-y divide-line">
              {(r.top_customers ?? []).slice(0, 8).map((c, i) => (
                <li
                  key={i}
                  className="flex items-center justify-between px-5 py-2.5"
                >
                  <span className="text-sm font-medium truncate">{c.name}</span>
                  <Money value={c.total} className="text-sm" />
                </li>
              ))}
            </ul>
          )}
        </Card>
      </div>
    </>
  );
}

function DailyChart({ points }: { points: RevenuePoint[] }) {
  if (points.length === 0) {
    return (
      <p className="text-sm text-ink-soft py-12 text-center">
        Not enough data for this period.
      </p>
    );
  }
  const data = points.map((p) => ({
    label: new Date(p.date).toLocaleDateString('en-IN', {
      day: 'numeric',
      month: 'short',
    }),
    Revenue: Number(p.total),
    Paid: Number(p.paid ?? 0),
  }));
  return (
    <div className="h-72">
      <ResponsiveContainer width="100%" height="100%">
        <LineChart data={data}>
          <CartesianGrid strokeDasharray="3 3" stroke="rgb(var(--line))" />
          <XAxis dataKey="label" stroke="rgb(var(--ink-soft))" fontSize={11} />
          <YAxis
            stroke="rgb(var(--ink-soft))"
            fontSize={11}
            width={50}
            tickFormatter={(v) => `₹${Math.round(v / 1000)}k`}
          />
          <Tooltip
            contentStyle={{
              background: 'rgb(var(--surface))',
              border: '1px solid rgb(var(--line))',
              borderRadius: 8,
              fontSize: 12,
            }}
            formatter={(v: number) => `₹${v.toLocaleString('en-IN')}`}
          />
          <Legend wrapperStyle={{ fontSize: 12 }} />
          <Line
            type="monotone"
            dataKey="Revenue"
            stroke="rgb(var(--brand-600))"
            strokeWidth={2}
            dot={false}
          />
          <Line
            type="monotone"
            dataKey="Paid"
            stroke="rgb(var(--brand-300))"
            strokeWidth={2}
            dot={false}
          />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}

function ExportCsv({ period }: { period: Period }) {
  const onClick = () => {
    const token = getToken() ?? '';
    const url = `${endpoints.reportExport}?type=bills&period=${period}&token=${encodeURIComponent(token)}`;
    window.open(url, '_blank', 'noopener');
  };
  return (
    <Button variant="outline" iconLeft={<Download className="h-4 w-4" />} onClick={onClick}>
      Export CSV
    </Button>
  );
}
